import React, { Key } from 'react';
import { EditIcon } from '@schuettflix/icons-react';
import { InlineLoadingSuspenseBoundary } from '@/shared/components/InlineLoadingSuspenseBoundary.tsx';
import { LabeledList } from '@schuettflix/react-components';

interface SectionSummarySharedProps {
    title: string;
    children?: React.ReactNode;
}

interface SectionSummaryNotEditableProps extends SectionSummarySharedProps {
    editable?: never;
    onEditClick?: never;
}

interface SectionSummaryEditableProps extends SectionSummarySharedProps {
    editable: true;
    onEditClick: () => void;
}

type SectionSummaryProps = SectionSummaryNotEditableProps | SectionSummaryEditableProps;

export const SectionSummary = ({ title, editable, onEditClick, children }: SectionSummaryProps) => {
    return (
        <div className="bg-light-gray-100 rounded p-4">
            <div className="mb-4 flex justify-between">
                <div className="flex items-center">
                    <h2 className="font-copy-md text" aria-disabled>
                        {title}
                    </h2>
                </div>

                {editable ? (
                    <span>
                        <EditIcon onClick={onEditClick} className="cursor-pointer" />
                    </span>
                ) : null}
            </div>
            <InlineLoadingSuspenseBoundary>
                <div className="text-subdued font-copy-sm">{children}</div>
            </InlineLoadingSuspenseBoundary>
        </div>
    );
};

export type SectionSummaryContentItem = {
    summaryContentItemId?: Key;
    label: string;
    value: React.ReactNode;
};

type SectionSummaryContentProps = {
    sectionSummaryContentList: SectionSummaryContentItem[];
    title?: string;
};

export const SectionSummaryContent = ({ sectionSummaryContentList, title }: SectionSummaryContentProps) => {
    return (
        <div className="flex flex-col gap-1">
            {title && <span className="font-copy-sm-strong">{title}</span>}
            <LabeledList size="md">
                {sectionSummaryContentList.map(summaryItem => (
                    <LabeledList.Row
                        key={summaryItem.summaryContentItemId ?? summaryItem.label}
                        label={<span className="block truncate">{summaryItem.label}</span>}
                        className="max-w-full"
                    >
                        <label className="block truncate">{summaryItem.value}</label>
                    </LabeledList.Row>
                ))}
            </LabeledList>
        </div>
    );
};
